import { useState, useEffect } from "react";
import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  Icon,
  IconButton,
  Box,
  Image,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import ProgressBar from "../partials/ProgressBar";
import {
  backgroundLight,
  primaryAlt,
  textSecondary,
  white,
} from "../../utils/colors";
import { FaArrowLeftLong } from "react-icons/fa6";
import LanguageButtonMain from "../buttons/LanguageButtonMain";
import OnboardingStepOne from "./partials/OnboardingStepOne";
import OnboardingStepTwo from "./partials/OnboardingStepTwo";
import OnboardingStepThree from "./partials/OnboardingStepThree";
import OnboardingStepSuccess from "./partials/OnboardingStepSuccess";
import OnboardingStepSkip from "./partials/OnboardingStepSkip";
import useIsMobile from "../../hooks/useIsMobile";
import dentacasa from "../../assets/icons/dentacasa.svg";

const MotionFlex = motion(Flex);
// Wrap Chakra UI components with motion to animate them
const MotionModalOverlay = motion(ModalOverlay);
const MotionModalContent = motion(ModalContent);

const Onboarding = ({ open, setOpen, tenant }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // STATE
  const [clinicName, setClinicName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [operatingCountry, setOperatingCountry] = useState("");
  const [senderIdentityEmail, setSenderIdentityEmail] = useState("");
  const [registratonEmail, setRegistrationEmail] = useState("");

  const [step, setStep] = useState(0);
  const [stepSkip, setStepSkip] = useState(false);
  const [stepSuccess, setStepSuccess] = useState(false);
  const [lastStep, setLastStep] = useState(0);
  const totalSteps = 3; // Assuming there are 5 steps excluding step zero
  const steps = totalSteps; // Total steps including step zero
  const currentStep = step;

  // REDUX
  // const dispatch = useDispatch();

  // const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  // const { loading: loadingTenant, tenant: tenant } = tenantDetailsBySlug;

  // const authLogin = useSelector((state) => state.authLogin);
  // const { userInfo } = authLogin;

  // HANDLERS

  const nextStep = (e) => {
    e.preventDefault();
    setStep(step + 1);
  };

  const previousStep = () => {
    // console.log("Going back from step:", step);

    setStep((prevStep) => {
      const newStep = Math.max(prevStep - 1, 0);
      // console.log("New step after going back:", newStep);
      return newStep; // Ensure the state update happens correctly
    });
  };

  const skipHandler = () => {
    setLastStep(step); // Store the current step before moving to the skip step
    setStep(""); // Skip step
    setStepSkip(true);
  };

  // SUBMIT

  // USE EFFECT
  useEffect(() => {
    if (tenant) {
      setClinicName(tenant.clinicName || "");
      setSupportEmail(tenant.supportEmail || tenant.registrationEmail || "");
      setOperatingCountry(tenant.operatingCountry || "");
      setRegistrationEmail(tenant.registrationEmail || "");
    }
  }, [tenant]);

  // useEffect(() => {
  //   if (stepSuccess) {
  //     setStepSkip(false);
  //   }
  // }, [stepSuccess]);

  // LOGS

  // console.log("active step", step);

  // console.log("clinicName", clinicName);
  // console.log("supportEmail", supportEmail);
  // console.log("operatingCountry", operatingCountry);

  return (
    <>
      <Modal
        isCentered
        isOpen={open}
        onClose={() => {
          if (step === 0) {
            console.log("Can't skip step 0");
          } else if (step === 1) {
            console.log("Can't skip step 1");
          } else if (step === 2) {
            console.log("Can't skip step 1");
          } else if (stepSuccess) {
            console.log("Can't skip step success");
          } else {
            skipHandler();
          }
        }}
        motionPreset="none" // Disable Chakra's internal animations
      >
        <MotionModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, delay: 0 }}
        />
        <MotionModalContent
          width="100%"
          maxW={["100vw", "80%"]}
          // miH={["100vh", "initial"]}
          borderRadius={["0rem", "3rem"]}
          m={[0, "initial"]}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 0, opacity: 0 }}
          transition={{ duration: 0.3, delay: 0 }}
        >
          <Flex
            w="100%"
            // h="100%"
            position="relative"
            overflow="hidden"
            align="center"
            justify="center"
            borderRadius={["0rem", "3rem"]}
            h={["100%", "initial"]}
            minH={["100vh", "initial"]}
          >
            <Flex className="gradientbg"></Flex>
            <Flex
              direction="column"
              align="center"
              justify="center"
              p="0vh 0 0 0"
              minH={["100vh", "90vh"]}
              w="100%"
            >
              <Flex
                direction="column"
                align="center"
                justify="center"
                w="100%"
                minH={["100vh", "90vh"]}
                h="100%"
                position="relative"
              >
                {isMobile && (
                  <Box
                    position={["absolute", "fixed"]}
                    top={["0rem", "2.5rem"]}
                    left={["0rem", "2.5rem"]}
                    // bg="green"
                    p={["1.5rem 1.5rem", "initial"]}
                    w={["100%", "100%"]}
                    zIndex={1000}
                  >
                    <Flex w="100%" align="center" justify="space-between">
                      <Box h={["20px", "22px"]} w="initial">
                        <Image src={dentacasa} h="100%" w="initial" />
                      </Box>

                      <Flex align="center" justify="flex-end" gap="0rem">
                        <LanguageButtonMain bg="rgba(255,255,255,0.3)" />
                      </Flex>
                    </Flex>
                  </Box>
                )}

                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <MotionFlex
                      key="step0"
                      initial={{ y: 0, opacity: 0 }}
                      animate={{
                        y: 0,
                        opacity: 1,
                        transition: { duration: 0.6, delay: 0.6 },
                      }} // Longer duration and delay on appearance
                      exit={{
                        y: 0,
                        opacity: 0,
                        transition: { duration: 0.2, delay: 0 },
                      }}
                    >
                      <OnboardingStepOne
                        setStep={setStep}
                        clinicName={clinicName}
                        setClinicName={setClinicName}
                        tenant={tenant}
                      />
                    </MotionFlex>
                  )}
                  {step === 1 && (
                    <MotionFlex
                      key="step2"
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -50, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <OnboardingStepTwo
                        tenant={tenant}
                        setStep={setStep}
                        supportEmail={supportEmail}
                        setSupportEmail={setSupportEmail}
                      />
                    </MotionFlex>
                  )}
                  {step === 2 && (
                    <MotionFlex
                      key="step3"
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -50, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <OnboardingStepThree
                        operatingCountry={operatingCountry}
                        setOperatingCountry={setOperatingCountry}
                        setStep={setStep}
                        setStepSuccess={setStepSuccess}
                        tenant={tenant}
                      />
                    </MotionFlex>
                  )}

                  {stepSuccess && (
                    <MotionFlex
                      key="step-success"
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -50, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <OnboardingStepSuccess
                        setOpen={setOpen}
                        senderIdentityEmail={senderIdentityEmail}
                      />
                    </MotionFlex>
                  )}

                  {stepSkip && (
                    <MotionFlex
                      key="step-skip"
                      initial={{ y: 50, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -50, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <OnboardingStepSkip
                        lastStep={lastStep}
                        onClose={() => setOpen(false)}
                        next={nextStep}
                        prev={previousStep}
                        setStep={setStep}
                        setStepSkip={setStepSkip}
                      />
                    </MotionFlex>
                  )}
                </AnimatePresence>

                {!isMobile && (
                  <Box position="absolute" top="2.5rem" right="2.5rem">
                    <LanguageButtonMain bg="rgba(255,255,255,0.3)" />
                  </Box>
                )}

                <Flex
                  w="100%"
                  align="center"
                  justify="center"
                  position="absolute"
                  bottom={["1.5rem", "2.5rem"]}
                  maxW="100%"
                  p={["0 1.5rem", "0 3rem"]}
                >
                  <Flex w={["60px", "120px"]} justify="flex-start">
                    {step !== 0 && !stepSuccess && !stepSkip && (
                      <IconButton
                        position="relative"
                        variant="ghost"
                        size="sm"
                        fontWeight="500"
                        borderRadius="10rem"
                        color={textSecondary}
                        transition="all 0.3s ease"
                        _hover={{ bg: primaryAlt, color: white }}
                        onClick={() => previousStep()}
                      >
                        <Icon as={FaArrowLeftLong} fontSize="sm" />
                      </IconButton>
                    )}
                  </Flex>

                  <Flex
                    w={["calc(100% - 120px)", "calc(100% - 240px)"]}
                    p={["14px 0px", "14px 0px"]}
                  >
                    {step !== 0 && !stepSkip && (
                      <ProgressBar
                        steps={steps}
                        currentStep={currentStep}
                        bg={backgroundLight}
                        color={primaryAlt}
                      />
                    )}
                  </Flex>

                  <Flex
                    justify="flex-end"
                    gap="0.5rem"
                    pl={["0.5rem", "1rem"]}
                    w={["60px", "120px"]}
                  >
                    {/* // STEPS COUNT */}
                    {!stepSuccess && (
                      <Flex
                        fontSize="xs"
                        fontWeight="500"
                        color={textSecondary}
                        align="center"
                        justify="flex-end"
                        textAlign="center"
                        w="100%"
                        minW="80px"
                        maxW="100%"
                        pr="0.5rem"
                        borderRadius="10rem"
                        bg="rgba(255, 255, 255, 0.3)"
                        // border={`1px solid ${newBorder}`}
                        // p="0.25rem 0.5rem"
                        position="relative"
                        zIndex="1"
                        textTransform="uppercase"

                        // textTransform="capitalize"
                      >
                        {stepSuccess
                          ? t("onboarding.stepSuccess")
                          : stepSkip
                          ? t("onboarding.stepSkip")
                          : `${step + 1} / ${steps}`}
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </MotionModalContent>
      </Modal>
    </>
  );
};

export default Onboarding;
